import { Injectable, NgZone } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import {
  MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar,
} from '@angular/material/snack-bar';
import { ToastDurationService } from './toast-duration.service';

/** A click on one of these inside a sticky toast is the control's own action, not a
 *  dismiss-the-toast gesture — matches CellCopyDirective's fall-through list. Forward-looking:
 *  no current toast carries such a control besides its own dismiss button (below). It exists because
 *  this proxy is what makes any component snackbar sticky under the 'persistent' preset, so a
 *  future toast with a real button/link is guarded at the one choke point. NOTE the one
 *  clickable component snack today, SnackDeleteComponent, is a `<button (click)="close()">`, so
 *  this guard skips it -- and it dismisses itself through its own handler, so nothing is lost. */
const INTERACTIVE = 'input, textarea, select, button, [role="button"], a[href], [contenteditable]';

/**
 * Thin proxy over MatSnackBar that applies the user's toast-duration preference to every toast
 * and, when a toast is sticky (resolved duration `undefined` — an error toast, or the user's
 * 'persistent' preset), wires a one-shot click-to-dismiss on the container. Centralising this
 * removes the hand-rolled getElementsByClassName listener loop that used to live in
 * NotificationService, and gives every snackbar consumer the preference for free.
 *
 * Inject this instead of MatSnackBar anywhere a toast is opened.
 */
@Injectable({ providedIn: 'root' })
export class AppSnackBarService {
  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly toast: ToastDurationService,
    private readonly zone: NgZone,
  ) {}

  open(
    message: string,
    action?: string,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<TextOnlySnackBar> {
    const { cfg, sticky } = this.scale(config);
    // Open inside the Angular zone so the overlay positions correctly (see the original
    // NotificationService note). Returned synchronously.
    let ref!: MatSnackBarRef<TextOnlySnackBar>;
    this.zone.run(() => {
      ref = this.snackBar.open(message, action, cfg);
      if (sticky) this.wireDismiss(ref);
    });
    return ref;
  }

  openFromComponent<T>(
    component: ComponentType<T>,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<T> {
    const { cfg, sticky } = this.scale(config);
    let ref!: MatSnackBarRef<T>;
    this.zone.run(() => {
      ref = this.snackBar.openFromComponent(component, cfg);
      if (sticky) this.wireDismiss(ref);
    });
    return ref;
  }

  dismiss(): void {
    this.snackBar.dismiss();
  }

  /** Replace config.duration with the preset-scaled value. Returns whether the toast is sticky
   *  (no auto-dismiss) so the caller wires click-to-dismiss. */
  private scale(config?: MatSnackBarConfig): { cfg: MatSnackBarConfig; sticky: boolean } {
    const cfg: MatSnackBarConfig = { ...(config ?? {}) };
    cfg.duration = this.toast.resolveDuration(config?.duration);
    return { cfg, sticky: cfg.duration === undefined };
  }

  /** Dismiss the (single, current) snackbar on a background click in its container. MatSnackBar
   *  shows at most one toast at a time, so the freshly-opened container is the last one in the DOM.
   *  A click that lands on an interactive control inside the toast (a component snackbar's own
   *  button/link) is that control's action, not a dismiss gesture — fall through and leave the
   *  toast open so the control keeps working. Not `{ once: true }`: an ignored interactive click
   *  must not consume the single dismiss listener. */
  private wireDismiss(ref: MatSnackBarRef<unknown>): void {
    const containers = document.getElementsByClassName('mat-mdc-snack-bar-container');
    const container = containers.item(containers.length - 1);
    container?.addEventListener('click', (e: Event) => {
      if ((e.target as Element | null)?.closest(INTERACTIVE)) return;
      ref.dismiss();
    });
  }
}
